import React, { createContext, useContext, useState } from 'react';
import CustomAlert from '../components/CustomAlert';

// Create the alert context
const AlertContext = createContext();

// Custom hook to use the alert context
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

// Alert provider component
export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState({
    isVisible: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false
  });

  // Show a simple alert (like the old alert() function)
  const showAlert = (message, type = 'info', title = '') => {
    return new Promise((resolve) => {
      setAlert({
        isVisible: true,
        type,
        title,
        message,
        onConfirm: () => {
          hideAlert();
          resolve(true);
        },
        onCancel: null,
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: false
      });
    });
  };

  // Show a confirmation dialog (like the old confirm() function)
  const showConfirm = (message, title = 'Confirm', confirmText = 'Yes', cancelText = 'No') => {
    return new Promise((resolve) => {
      setAlert({
        isVisible: true,
        type: 'warning',
        title,
        message,
        onConfirm: () => {
          hideAlert();
          resolve(true);
        },
        onCancel: () => {
          hideAlert();
          resolve(false);
        },
        confirmText,
        cancelText,
        showCancel: true
      });
    });
  };

  // Show success alert
  const showSuccess = (message, title = 'Success') => {
    return showAlert(message, 'success', title);
  };

  // Show error alert
  const showError = (message, title = 'Error') => {
    return showAlert(message, 'error', title);
  };

  // Show warning alert
  const showWarning = (message, title = 'Warning') => {
    return showAlert(message, 'warning', title);
  };

  // Show info alert
  const showInfo = (message, title = 'Information') => {
    return showAlert(message, 'info', title);
  };

  // Hide the current alert
  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  // Context value
  const value = {
    showAlert,
    showConfirm,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideAlert
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onConfirm={alert.onConfirm}
        onCancel={alert.onCancel}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        showCancel={alert.showCancel}
      />
    </AlertContext.Provider>
  );
};
